// NavItem.jsx
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { handlerDrawerOpen, useGetMenuMaster } from '../../../../api/menu';
import useConfig from '../../../../hooks/useConfig';

export default function NavItem({ item, level = 1, isParents = false, setSelectedID }) {
  const downMD = useMediaQuery(theme => theme.breakpoints.down('md'));
  const ref = useRef(null);
  const { pathname } = useLocation();
  const { borderRadius } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isSelected = !!matchPath({ path: item?.link || item.url, end: false }, pathname);
  const [hoverStatus, setHover] = useState(false);

  useEffect(() => {
    const compareSize = () => {
      if (ref.current) setHover(ref.current.scrollWidth > ref.current.clientWidth);
    };
    compareSize();
    window.addEventListener('resize', compareSize);
    return () => window.removeEventListener('resize', compareSize);
  }, []);

  const Icon = item?.icon;
  const itemIcon = Icon ? (
    <Icon
      stroke={1.5}
      size={drawerOpen ? (level === 1 ? '18px' : '14px') : '18px'}
      style={{
        color: isSelected ? '#FFFFFF' : '#95A5A6',
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: isSelected ? 7 : 5,
        height: isSelected ? 7 : 5,
        color: isSelected ? '#FFFFFF !important' : '#95A5A6 !important',
        transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1), height 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    />
  );

  const itemHandler = () => {
    if (downMD) handlerDrawerOpen(false);
    if (isParents && setSelectedID) setSelectedID();
  };

  return (
    <>
      <ListItemButton
        component={Link}
        to={item.url}
        target={item.target ? '_blank' : '_self'}
        disabled={item.disabled}
        onClick={itemHandler}
        sx={{
          position: 'relative',
          borderRadius: `${borderRadius}px`,
          mb: '2px',
          py: level === 1 ? '4px' : '3px',
          pl: drawerOpen ? (level === 1 ? '12px' : `${12 + level * 8}px`) : '7px',
          pr: '7px',
          color: isSelected ? '#FFFFFF !important' : '#2C3E50 !important',
          backgroundColor: isSelected ? '#3498DB !important' : 'transparent',
          borderLeft: isSelected ? '3px solid #3498DB' : '3px solid transparent',
          transition: 'background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-left-color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: isSelected ? 'pulseGlow 2s infinite' : 'none',
          '&:hover': {
            backgroundColor: '#2980B9 !important',
            color: '#FFFFFF !important',
            '& .menu-icon': {
              transform: 'scale(1.1)'
            }
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#3498DB !important',
            color: '#FFFFFF !important'
          }
        }}
      >
        <ListItemIcon
          className="menu-icon"
          sx={{
            minWidth: level === 1 ? '28px' : '20px',
            color: isSelected ? '#FFFFFF !important' : '#95A5A6 !important',
            display: 'flex',
            justifyContent: 'center',
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {itemIcon}
        </ListItemIcon>

        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <Tooltip title={item.title} disableHoverListener={!hoverStatus}>
            <ListItemText
              primary={
                <Typography
                  ref={ref}
                  noWrap
                  variant="body2"
                  fontWeight={isSelected ? 600 : 500}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: isSelected ? '#FFFFFF !important' : 'inherit',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit'
                  }}
                >
                  {item.title}
                </Typography>
              }
              secondary={
                item.caption && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: '1px',
                      color: '#7F8C8D !important',
                      fontSize: '0.85rem',
                      fontFamily: 'inherit'
                    }}
                  >
                    {item.caption}
                  </Typography>
                )
              }
            />
          </Tooltip>
        )}

        {drawerOpen && item.chip && (
          <Chip
            size="small"
            label={item.chip.label}
            sx={{
              height: '20px',
              fontSize: '0.85rem',
              fontFamily: 'inherit',
              fontWeight: 500,
              bgcolor: 'rgba(52, 152, 219, 0.2)',
              color: '#3498DB !important',
              borderRadius: '3px'
            }}
          />
        )}
      </ListItemButton>
      <style jsx global>{`
        @keyframes pulseGlow {
          0% { box-shadow: 0 0 0 0 rgba(52, 152, 219, 0.4); }
          70% { box-shadow: 0 0 0 8px rgba(52, 152, 219, 0); }
          100% { box-shadow: 0 0 0 0 rgba(52, 152, 219, 0); }
        }
      `}</style>
    </>
  );
}

NavItem.propTypes = {
  item: PropTypes.any.isRequired,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.func
};